import React, { memo, useCallback } from "react";

import { faPencil } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";

import { Column } from "components/Layout";
import TabRow from "components/TabSelector/TabRow";

import { filterTabsBySaleType } from "constants/sale";
import { ScreenType } from "constants/screen";
import { userTypes } from "constants/users";

import { SecurityTrimmedNavigationTab } from "containers/LivestockSales/SecurityTrimmedNavigationTab";

import { isUserOfType } from "lib/auth";
import {
  getLivestockSaleId,
  getSaleyardAuctionRoute,
  getSaleyardName,
  openSaleModal,
} from "lib/navigation";

import { getAuth, getCurrentSale } from "selectors";

import {
  useHasBillingRelatedPermissions,
  useHasManualChargesPermission,
} from "hooks";
import { useHasSelectedDesktopView } from "hooks/useScreenSize";

const getMobileTabs = (auth, hasManualChargesPermission) => [
  {
    id: "edit-sale",
    title: <FontAwesomeIcon icon={faPencil} />,
    display: true,
    onClick: () => openSaleModal(getLivestockSaleId()),
  },
  {
    id: "billing",
    title: "Billing",
    display: Boolean(isUserOfType([userTypes.LIVESTOCK_AGENT], auth)),
    component: SecurityTrimmedNavigationTab,
    hasPermissionSelector: () => hasManualChargesPermission,
  },
];

const getDesktopTabs = (auth, hasBillingRelatedPermission) => [
  {
    id: "edit-sale",
    title: <FontAwesomeIcon icon={faPencil} />,
    display: true,
    onClick: () => openSaleModal(getLivestockSaleId()),
  },
  {
    id: "billing",
    title: "Billing",
    display: Boolean(isUserOfType([userTypes.LIVESTOCK_AGENT], auth)),
    component: SecurityTrimmedNavigationTab,
    hasPermissionSelector: () => hasBillingRelatedPermission,
  },
];

function SundrySaleTabsComponent() {
  const auth = useSelector(getAuth);
  const sale = useSelector(getCurrentSale);

  const hasBillingRelatedPermission = useHasBillingRelatedPermissions();
  const hasManualChargesPermission = useHasManualChargesPermission();

  const isDesktop = useHasSelectedDesktopView();

  const { params } = useRouteMatch();
  const history = useHistory();

  const setSection = useCallback(
    section => {
      history.push(
        `${getSaleyardAuctionRoute(
          getSaleyardName(),
          getLivestockSaleId(),
        )}/${section}`,
      );
    },
    [history],
  );
  const { section } = params;
  const { sale_type: saleType } = sale;

  const [tabsByUser, showTabs] = React.useMemo(() => {
    const tabsByUser = isDesktop
      ? getDesktopTabs(auth, hasBillingRelatedPermission).filter(
          filterTabsBySaleType(saleType, ScreenType.DESKTOP),
        )
      : getMobileTabs(auth, hasManualChargesPermission).filter(
          filterTabsBySaleType(saleType, ScreenType.MOBILE),
        );

    const showTabs = tabsByUser.filter(t => t.display).length > 0;
    return [tabsByUser, showTabs];
  }, [
    isDesktop,
    saleType,
    auth,
    hasBillingRelatedPermission,
    hasManualChargesPermission,
  ]);

  return (
    <>
      {showTabs && (
        <Column printHidden>
          <TabRow
            tabs={tabsByUser}
            selectedTab={section}
            setSelectedTab={setSection}
            backgroundColor="gray95"
          />
        </Column>
      )}
    </>
  );
}

export default memo(SundrySaleTabsComponent);
